import {
  Add,
  CheckCircle,
  CloseOutlined,
  EditOutlined,
} from '@mui/icons-material';
import { LabelOutlined as LabelOutlinedIcon } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useContext } from 'react';

import {
  ImpactCo2Answer,
  ValuesAccommodation,
  ValuesAccommodationPublic,
  ValuesBuilding,
  ValuesBuildingHeating,
  ValuesCargo,
  ValuesCateringBeverage,
  ValuesCateringMeal,
  ValuesDigital,
  ValuesEnergy,
  ValuesEquipment,
  ValuesGenerator,
  ValuesMobility,
  ValuesMobilityPublic,
  ValuesNonRecyclableWaste,
  ValuesOfficeSuppliesNew,
  ValuesOfficeSuppliesRefurbish,
  ValuesRecyclableWaste,
  ValuesScenographyNoRecyclable,
  ValuesScenographyRecyclable,
  ValuesTeamMobility,
  ValuesWater,
} from '@/domain/impact-co2/entities/question.entity';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { QuizLabel } from '@/ui/pages/app/components/quiz-panel/co2/components/QuizLabel';
import { ScoreCo2Label } from '@/ui/pages/app/components/quiz-panel/co2/components/ScoreCo2Label';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { TagsView } from './TagsView';

const getAnsweredBoxStyle = ({
  editing,
  panelOpen,
}: {
  editing: boolean;
  panelOpen: boolean;
}) => {
  if (panelOpen) {
    return {
      border: editing
        ? `1px solid ${ColorsRaw.anthracite.Anthracite_5}`
        : `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
      borderRadius: '12px',
      p: 2,
      mt: 2,
      backgroundColor: editing ? '' : ColorsRaw.anthracite.Anthracite_2,
    };
  }

  return {
    border: `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
    borderRadius: '12px',
    p: 2,
    mt: 2,
  };
};

interface QEProps {
  answer: ImpactCo2Answer<
    | ValuesAccommodation
    | ValuesAccommodationPublic
    | ValuesBuilding
    | ValuesCargo
    | ValuesDigital
    | ValuesCateringBeverage
    | ValuesCateringMeal
    | ValuesEnergy
    | ValuesEquipment
    | ValuesGenerator
    | ValuesMobility
    | ValuesMobilityPublic
    | ValuesNonRecyclableWaste
    | ValuesRecyclableWaste
    | ValuesScenographyNoRecyclable
    | ValuesScenographyRecyclable
    | ValuesBuildingHeating
    | ValuesOfficeSuppliesNew
    | ValuesOfficeSuppliesRefurbish
    | ValuesTeamMobility
    | ValuesWater
  >;
  removeFromEditing: (id: number) => void;
  form: React.ReactNode;
  title: string;
  setDisplayTag?: () => void;
  index?: number;
  hideIndex?: boolean;
}

export function QuizEdition({
  removeFromEditing,
  answer,
  form,
  title,
  setDisplayTag,
}: QEProps) {
  const { panelOpen } = useContext(ImpactQuestionContext);

  return (
    <Box sx={getAnsweredBoxStyle({ editing: true, panelOpen })}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'top'}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 20, mb: 1 }}>
          {title}
        </Typography>

        <Box
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'top'}
        >
          {panelOpen && setDisplayTag && (
            <Button
              sx={{
                p: 1,
                minWidth: 0,
                borderRadius: '50%',
                width: 'fit-content',
                height: 'fit-content',
              }}
              onClick={setDisplayTag}
            >
              <LabelOutlinedIcon sx={{ fontSize: 18 }} />
            </Button>
          )}
          <Button
            sx={{
              p: 1,
              minWidth: 0,
              borderRadius: '50%',
              width: 'fit-content',
              height: 'fit-content',
            }}
            onClick={() => removeFromEditing(answer.id)}
          >
            <CloseOutlined sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>
      {form}
    </Box>
  );
}

interface QNEProps {
  addToEditing: (id: number) => void;
  answeredValues: string[];
  index?: number;
  hideIndex?: boolean;
  title: string;
  answer: ImpactCo2Answer<
    | ValuesAccommodation
    | ValuesAccommodationPublic
    | ValuesBuilding
    | ValuesCargo
    | ValuesCateringBeverage
    | ValuesCateringMeal
    | ValuesDigital
    | ValuesEnergy
    | ValuesEquipment
    | ValuesGenerator
    | ValuesMobility
    | ValuesMobilityPublic
    | ValuesNonRecyclableWaste
    | ValuesRecyclableWaste
    | ValuesScenographyNoRecyclable
    | ValuesScenographyRecyclable
    | ValuesBuildingHeating
    | ValuesOfficeSuppliesNew
    | ValuesOfficeSuppliesRefurbish
    | ValuesTeamMobility
    | ValuesWater
  >;
  quizLabel?: string;
  score?: number;
}

export function QuizNotEdition({
  addToEditing,
  answeredValues,
  title,
  answer,
  quizLabel,
  score,
}: QNEProps) {
  const { panelOpen } = useContext(ImpactQuestionContext);
  return (
    <Box sx={getAnsweredBoxStyle({ editing: false, panelOpen })}>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        marginBottom={1}
      >
        <Typography sx={{ color: ColorsRaw.main.Navy_Light, fontSize: 16 }}>
          {title}
        </Typography>
        {panelOpen ? (
          <Button
            sx={{
              p: 1,
              minWidth: 0,
              borderRadius: '50%',
              width: 'fit-content',
              height: 'fit-content',
            }}
            onClick={() => addToEditing(answer.id)}
          >
            <EditOutlined sx={{ fontSize: 18 }} />
          </Button>
        ) : (
          <CheckCircle color="primary" sx={{ fontSize: 18 }} />
        )}
      </Box>
      {answer.values.tags?.length > 0 && <TagsView tags={answer.values.tags} />}
      {(quizLabel || score !== undefined) && (
        <Stack
          spacing={1}
          mb={2}
          direction="row"
          justifyContent="space-between"
          alignContent="center"
        >
          <QuizLabel label={quizLabel} />
          <ScoreCo2Label score={score ?? 0} />
        </Stack>
      )}
      {answeredValues.map((value, i) => (
        <Typography
          key={'anwseredValue' + i}
          sx={{ fontSize: 20, fontWeight: 'bold' }}
        >
          {value}
        </Typography>
      ))}
    </Box>
  );
}

interface NQEProps {
  form: React.ReactNode;
  answersLength: number;
  title: string;
  setDisplayTag?: () => void;
  hideIndex?: boolean;
}

export function NewQuizEdition({ form, title, setDisplayTag }: NQEProps) {
  const { panelOpen } = useContext(ImpactQuestionContext);

  return (
    <Box
      sx={{
        border: panelOpen
          ? `1px solid ${ColorsRaw.anthracite.Anthracite_5}`
          : `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
        borderRadius: '12px',
        p: 2,
        mt: 2,
        backgroundColor: panelOpen ? '' : ColorsRaw.anthracite.Anthracite_2,
      }}
    >
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{
          mb: panelOpen ? 1 : 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: panelOpen ? 'bold' : '',
            fontSize: panelOpen ? 20 : 16,
            mb: 0,
            color: panelOpen ? '' : ColorsRaw.main.Navy_Light,
          }}
        >
          {title}
        </Typography>
        {panelOpen && setDisplayTag && (
          <Button
            sx={{
              p: 1,
              minWidth: 0,
              borderRadius: '50%',
              width: 'fit-content',
              height: 'fit-content',
            }}
            onClick={setDisplayTag}
          >
            <LabelOutlinedIcon color="primary" />
          </Button>
        )}
      </Box>
      {panelOpen && form}
    </Box>
  );
}

interface ANQProps {
  setDisplayNew: React.Dispatch<React.SetStateAction<boolean>>;
  buttonTitle: string;
}

export function AddNewQuiz({ setDisplayNew, buttonTitle }: ANQProps) {
  const { panelOpen } = useContext(ImpactQuestionContext);

  return (
    <>
      {/* display add question button only if form enable */}
      {panelOpen && (
        <FSSecondaryButton
          sx={{ mt: 1 }}
          startIcon={<Add />}
          onClick={() => setDisplayNew(true)}
        >
          {buttonTitle}
        </FSSecondaryButton>
      )}
    </>
  );
}
